import React, {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components/native';
import {RootState} from '../../redux/store';
import {Animated, BackHandler, Easing, Platform} from 'react-native';
import {LoadingProps} from '../../interfaces/connected-components/loading';
import Loader from '../../components/loader';

/**
 * A styled container view that covers the entire screen and centers its content.
 */
const Container = styled(Animated.View)`
  width: 100%;
  height: 100%;
  z-index: 9999;
  position: absolute !important;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

/**
 * A global loading component that displays an activity indicator when loading is in progress.
 * It wraps the main content of the application and is shown as a modal overlay.
 *
 * @param {LoadingProps} loading - The loading state from the Redux store.
 * @returns {React.ReactElement} The GlobalLoading component.
 */
const GlobalLoading = ({loading}: LoadingProps): React.ReactElement => {
  /**
   * Destructure the 'isVisible' property from the loading object.
   */
  const {isVisible} = loading || {};
  const animationController = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(animationController, {
      toValue: isVisible ? 1 : 0,
      useNativeDriver: Platform.OS !== 'web',
      duration: 300,
      easing: Easing.ease,
    }).start();
  }, [isVisible]);
  /**
   * Interpolates the bottom position of the modal during the animation.
   * @type {Animated.AnimatedInterpolation<string | number>}
   */

  const opacity = animationController.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  /**
   * Disable the back button behavior if the loader is running.
   */
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const onBackPress = () => isVisible;

    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      onBackPress,
    );

    return () => {
      subscription.remove();
    };
  }, [isVisible]);

  return (
    <Container style={{opacity, pointerEvents: isVisible ? 'auto' : 'none'}}>
      <Container>
        <Loader />
      </Container>
    </Container>
  );
};

/**
 * Maps the Redux store state to the 'loading' prop of the GlobalLoading component.
 *
 * @param {RootState} state - The entire Redux store state.
 */
const mapStateToProps = (state: RootState) => ({
  loading: state.loading,
});

export default connect(mapStateToProps, null)(GlobalLoading);
